import { Component } from '@theme/component';
import { sectionRenderer } from '@theme/section-renderer';
import { requestIdleCallback, viewTransition } from '@theme/utilities';
import { ThemeEvents } from '@theme/events';
import { PaginatedListAspectRatioHelper } from '@theme/paginated-list-aspect-ratio';

/**
 * A custom element that renders a paginated list of items.
 *
 * @typedef {object} Refs
 * @property {HTMLUListElement} [grid] - The grid element.
 * @property {HTMLSpanElement} [viewMorePrevious] - The view more previous button.
 * @property {HTMLSpanElement} [viewMoreNext] - The view more next button.
 * @property {HTMLElement[]} [cards] - The cards elements.
 *
 * @extends Component<Refs>
 */
export default class PaginatedList extends Component {
  /**
   * @type {Map<number, string>}
   */
  pages = new Map();

  /** @type {IntersectionObserver | undefined} */
  infinityScrollObserver;

  /** @type {boolean} */
  #isRendering = false;

  /** @type {PaginatedListAspectRatioHelper} */
  #aspectRatioHelper;

  /** @type {number | null} */
  #nextFrontierPage = null;

  /** @type {number | null} */
  #previousFrontierPage = null;

  /** @type {boolean} */
  #hasMoreNext = true;

  /** @type {boolean} */
  #hasMorePrevious = true;

  /** @type {number} */
  #nextFailures = 0;

  /** @type {number} */
  #previousFailures = 0;

  /** @type {Set<number>} Pages that have been fetched but contained invalid/empty responses */
  #failedPages = new Set();

  /** @type {Map<number, number>} Tracks how many times each page has failed (for retry logic) */
  #pageFailCounts = new Map();

  /** @type {number} Max retries before permanently blacklisting a page */
  static MAX_PAGE_RETRIES = 3;

  /** @type {Set<number>} Pages currently being fetched (prevents duplicate in-flight requests) */
  #inFlightPages = new Set();

  /** @type {Element[]} Leftover unique cards from a partially-consumed page (forward direction) */
  #pendingNextCards = [];

  /** @type {Element[]} Leftover unique cards from a partially-consumed page (backward direction) */
  #pendingPreviousCards = [];

  connectedCallback() {
    super.connectedCallback();

    /** @type {HTMLElement | null} */
    const templateCard = this.querySelector('[ref="cardGallery"]');
    if (templateCard) {
      this.#aspectRatioHelper = new PaginatedListAspectRatioHelper({
        templateCard,
      });
    }

    const { grid } = this.refs;
    console.log('[Pagination] init', {
      lastPage: grid?.dataset.lastPage,
      cards: this.refs.cards?.length,
    });

    this.#prefetchPage('next');
    this.#prefetchPage('previous');
    this.#observeViewMore();

    // Listen for filter updates to clear cached pages
    document.addEventListener(ThemeEvents.FilterUpdate, this.#handleFilterUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.infinityScrollObserver) {
      this.infinityScrollObserver.disconnect();
    }
    document.removeEventListener(ThemeEvents.FilterUpdate, this.#handleFilterUpdate);
    this.#removeLoadingImage();
  }

  // ---------------------------------------------------------------------------
  // Loading indicator
  // ---------------------------------------------------------------------------

  #addLoadingImageAfterGrid() {
    const { grid } = this.refs;
    if (!grid) return;

    this.#removeLoadingImage();

    const loadingWrapper = document.createElement('div');
    loadingWrapper.className = 'loading-image-wrapper';
    loadingWrapper.setAttribute('data-pagination-loader', 'true');
    loadingWrapper.style.cssText = 'display: flex; justify-content: center; align-items: center; padding: 3rem; width: 100%;';

    const loadingInner = document.createElement('div');
    loadingInner.style.cssText = 'width: 80px; height: 80px;';

    const mainLoading = document.createElement('div');
    mainLoading.className = 'main-loading';

    const loaderImg = document.createElement('img');
    loaderImg.src = 'https://cdn.shopify.com/s/files/1/0148/9585/files/blenders-loader_5487f575-9956-4ad1-b2c6-66945fa048f7.gif?v=1749575848';
    loaderImg.alt = 'Loading...';
    loaderImg.style.cssText = 'width: 80px; height: 80px; display: block; margin: 0 auto;';

    mainLoading.appendChild(loaderImg);
    loadingInner.appendChild(mainLoading);
    loadingWrapper.appendChild(loadingInner);

    grid.parentNode?.insertBefore(loadingWrapper, grid.nextSibling);
  }

  #removeLoadingImage() {
    const existingLoader = document.querySelector('[data-pagination-loader="true"]');
    if (existingLoader) {
      existingLoader.remove();
    }
  }

  // ---------------------------------------------------------------------------
  // Intersection Observer
  // ---------------------------------------------------------------------------

  #observeViewMore() {
    const { viewMorePrevious, viewMoreNext } = this.refs;
    if (!viewMorePrevious && !viewMoreNext) return;

    if (!this.infinityScrollObserver) {
      this.infinityScrollObserver = new IntersectionObserver(
        async (entries) => {
          if (viewTransition.current) await viewTransition.current;
          if (this.#isRendering) {
            console.log('[Pagination] observer skipped — already rendering');
            return;
          }

          for (const entry of entries) {
            if (!entry.isIntersecting) continue;

            const { viewMorePrevious, viewMoreNext } = this.refs;

            if (entry.target === viewMorePrevious && this.#hasMorePrevious) {
              this.#isRendering = true;
              try {
                await this.#renderPreviousPage();
              } finally {
                this.#isRendering = false;
                this.#reobserveViewMore();
              }
            } else if (entry.target === viewMoreNext && this.#hasMoreNext) {
              this.#isRendering = true;
              try {
                await this.#renderNextPage();
              } finally {
                this.#isRendering = false;
                this.#reobserveViewMore();
              }
            }
          }
        },
        { rootMargin: '200px' }
      );
    }

    if (viewMorePrevious) this.infinityScrollObserver.observe(viewMorePrevious);
    if (viewMoreNext) this.infinityScrollObserver.observe(viewMoreNext);
  }

  /**
   * After rendering, unobserve + re-observe the sentinels so the
   * IntersectionObserver fires again if the sentinel is still in view.
   */
  #reobserveViewMore() {
    if (!this.infinityScrollObserver) return;
    const { viewMorePrevious, viewMoreNext } = this.refs;

    if (viewMorePrevious) {
      this.infinityScrollObserver.unobserve(viewMorePrevious);
      this.infinityScrollObserver.observe(viewMorePrevious);
    }
    if (viewMoreNext) {
      this.infinityScrollObserver.unobserve(viewMoreNext);
      this.infinityScrollObserver.observe(viewMoreNext);
    }
  }

  // ---------------------------------------------------------------------------
  // Page info helpers
  // ---------------------------------------------------------------------------

  /**
   * @param {{ page: number, url?: URL } | undefined} pageInfo
   * @returns {boolean}
   */
  #shouldUsePage(pageInfo) {
    if (!pageInfo) return false;

    const { grid } = this.refs;
    const lastPage = grid?.dataset.lastPage;

    if (!lastPage || pageInfo.page < 1 || pageInfo.page > Number(lastPage)) return false;
    if (Number(lastPage) <= 1) return false;

    return true;
  }

  /**
   * Determines the next/previous page number based on the last/first card's data-page attribute.
   *
   * @param {"previous" | "next"} type
   * @returns {{ page: number, url: URL } | undefined}
   */
  #getPage(type) {
    const { cards } = this.refs;
    const isPrevious = type === 'previous';

    if (!Array.isArray(cards) || cards.length === 0) return;

    const targetCard = cards[isPrevious ? 0 : cards.length - 1];
    if (!targetCard) return;

    const currentCardPage = Number(targetCard.dataset.page);
    const page = isPrevious ? currentCardPage - 1 : currentCardPage + 1;

    const url = new URL(window.location.href);
    url.searchParams.set('page', page.toString());
    url.hash = '';

    return { page, url };
  }

  /**
   * Build a page info object from an explicit page number.
   *
   * @param {number} pageNumber
   * @returns {{ page: number, url: URL }}
   */
  #pageInfoFromNumber(pageNumber) {
    const url = new URL(window.location.href);
    url.searchParams.set('page', pageNumber.toString());
    url.hash = '';
    return { page: pageNumber, url };
  }

  /**
   * Get the next page to fetch, using the frontier page if available,
   * otherwise falling back to the DOM-based #getPage.
   *
   * @param {"previous" | "next"} type
   * @param {number | null} frontierPage
   * @returns {{ page: number, url: URL } | undefined}
   */
  #getNextPageInfo(type, frontierPage) {
    if (frontierPage !== null) {
      const page = type === 'next' ? frontierPage + 1 : frontierPage - 1;
      return this.#pageInfoFromNumber(page);
    }
    return this.#getPage(type);
  }

  // ---------------------------------------------------------------------------
  // Fetching
  // ---------------------------------------------------------------------------

  /**
   * Pre-fetch the next or previous page into the cache.
   *
   * @param {"previous" | "next"} type
   */
  async #prefetchPage(type) {
    const page = this.#getPage(type);
    if (!page || !this.#shouldUsePage(page)) return;
    await this.#fetchSpecificPage(page.page, page.url);
  }

  /**
   * @param {number} pageNumber
   * @param {URL} [url]
   * @returns {Promise<void>}
   */
  async #fetchSpecificPage(pageNumber, url = undefined) {
    if (this.pages.has(pageNumber)) {
      console.log(`[Pagination] fetch(${pageNumber}) cached`);
      return;
    }
    if (this.#failedPages.has(pageNumber)) {
      console.log(`[Pagination] fetch(${pageNumber}) skip — previously failed`);
      return;
    }
    if (this.#inFlightPages.has(pageNumber)) {
      console.log(`[Pagination] fetch(${pageNumber}) skip — already in flight`);
      // Wait for the in-flight request to finish
      while (this.#inFlightPages.has(pageNumber)) {
        await new Promise((r) => setTimeout(r, 100));
      }
      return;
    }

    const pageInfo = { page: pageNumber, url };

    if (!url) {
      const newUrl = new URL(window.location.href);
      newUrl.searchParams.set('page', pageNumber.toString());
      newUrl.hash = '';
      pageInfo.url = newUrl;
    }

    if (!this.#shouldUsePage(pageInfo)) {
      console.log(`[Pagination] fetch(${pageNumber}) skip — invalid page`);
      return;
    }

    this.#inFlightPages.add(pageNumber);
    try {
      const maxRetries = PaginatedList.MAX_PAGE_RETRIES;
      const previousFails = this.#pageFailCounts.get(pageNumber) || 0;
      const attemptsLeft = maxRetries - previousFails;

      for (let attempt = 1; attempt <= attemptsLeft; attempt++) {
        try {
          console.log(`[Pagination] fetch(${pageNumber}) loading... (attempt ${attempt}/${attemptsLeft})`);
          const pageContent = await sectionRenderer.getSectionHTML(this.sectionId, false, pageInfo.url);

          if (pageContent && pageContent.includes('ref="grid"')) {
            this.pages.set(pageNumber, pageContent);
            this.#pageFailCounts.delete(pageNumber);
            console.log(`[Pagination] fetch(${pageNumber}) OK — ${pageContent.length} chars`);
            return;
          }

          console.warn(`[Pagination] fetch(${pageNumber}) invalid response (attempt ${attempt})`);
        } catch (err) {
          console.warn(`[Pagination] fetch(${pageNumber}) error (attempt ${attempt})`, err);
        }

        // Exponential backoff: 1s, 2s, 4s
        if (attempt < attemptsLeft) {
          const delay = 1000 * Math.pow(2, attempt - 1);
          await new Promise((r) => setTimeout(r, delay));
        }
      }

      // All attempts exhausted — permanently blacklist this page
      this.#pageFailCounts.set(pageNumber, maxRetries);
      this.#failedPages.add(pageNumber);
      console.warn(`[Pagination] fetch(${pageNumber}) permanently failed after ${maxRetries} total attempts`);
    } finally {
      this.#inFlightPages.delete(pageNumber);
    }
  }

  // ---------------------------------------------------------------------------
  // Deduplication
  // ---------------------------------------------------------------------------

  /**
   * Collect identifiers of items already visible in the grid.
   *
   * @param {HTMLElement} grid
   * @returns {{ seenCollections: Set<string>, seenProductIds: Set<string> }}
   */
  #getSeenItems(grid) {
    const seenCollections = new Set();
    const seenProductIds = new Set();
    for (const card of grid.querySelectorAll('[ref="cards[]"]')) {
      const el = /** @type {HTMLElement} */ (card);
      // Skip cards hidden via CSS (e.g. 12th card hidden on desktop with hero)
      if (el.offsetParent === null && !el.hasAttribute('data-hero-card')) continue;
      const name = el.dataset.collectionName;
      if (name) seenCollections.add(name);
      const pid = el.dataset.productId;
      if (pid) seenProductIds.add(pid);
    }
    return { seenCollections, seenProductIds };
  }

  /**
   * Filter out cards whose product or collection is already visible.
   *
   * @param {Element[]} elements
   * @param {{ seenCollections: Set<string>, seenProductIds: Set<string> }} seen
   * @returns {Element[]}
   */
  #filterDuplicateCards(elements, seen) {
    return elements.filter((el) => {
      const h = /** @type {HTMLElement} */ (el);
      const pid = h.dataset?.productId;
      if (pid) {
        if (seen.seenProductIds.has(pid)) return false;
        seen.seenProductIds.add(pid);
      }
      const name = h.dataset?.collectionName;
      if (name) {
        if (seen.seenCollections.has(name)) return false;
        seen.seenCollections.add(name);
      }
      return true;
    });
  }

  // ---------------------------------------------------------------------------
  // Rendering
  // ---------------------------------------------------------------------------

  async #renderNextPage() {
    const { grid } = this.refs;
    if (!grid) return;

    const TARGET_CARDS = 12;
    let totalNewCardsAdded = 0;
    let frontierPage = this.#nextFrontierPage;
    const seen = this.#getSeenItems(grid);
    const cardsBuffer = [];

    console.log('[Pagination] renderNext START', {
      frontier: frontierPage,
      seenCollections: seen.seenCollections.size,
      seenProducts: seen.seenProductIds.size,
      lastPage: grid.dataset.lastPage,
      gridChildren: grid.children.length,
    });

    this.#addLoadingImageAfterGrid();

    try {
      while (totalNewCardsAdded < TARGET_CARDS) {
        // First drain any leftover cards from a previous partial page
        if (this.#pendingNextCards.length > 0) {
          const remaining = TARGET_CARDS - totalNewCardsAdded;
          const batch = this.#pendingNextCards.splice(0, remaining);
          console.log(`[Pagination] draining ${batch.length} pending cards (${this.#pendingNextCards.length} left)`);
          if (batch.length > 0) {
            for (const el of batch) {
              const h = /** @type {HTMLElement} */ (el);
              const name = h.dataset?.collectionName;
              if (name) seen.seenCollections.add(name);
              const pid = h.dataset?.productId;
              if (pid) seen.seenProductIds.add(pid);
            }
            cardsBuffer.push(...batch);
            totalNewCardsAdded += batch.length;
          }
          continue;
        }

        const nextPage = this.#getNextPageInfo('next', frontierPage);
        const shouldUse = nextPage ? this.#shouldUsePage(nextPage) : false;
        console.log('[Pagination] loop', { page: nextPage?.page, shouldUse, added: totalNewCardsAdded });
        if (!nextPage || !shouldUse) break;

        let nextPageItemElements = this.#getGridForPage(nextPage.page);

        if (!nextPageItemElements) {
          await this.#fetchSpecificPage(nextPage.page, nextPage.url);
          nextPageItemElements = this.#getGridForPage(nextPage.page);
          if (!nextPageItemElements) {
            console.log(`[Pagination] page ${nextPage.page} — no grid items after fetch`);
            break;
          }
        }

        frontierPage = nextPage.page;

        const newCards = this.#filterDuplicateCards(nextPageItemElements, seen);
        const remaining = TARGET_CARDS - totalNewCardsAdded;
        const cardsToAdd = newCards.slice(0, remaining);
        // Store leftover unique cards for the next render cycle
        if (newCards.length > remaining) {
          this.#pendingNextCards = newCards.slice(remaining);
        }
        console.log(`[Pagination] page ${nextPage.page}: ${nextPageItemElements.length} raw, ${newCards.length} unique, adding ${cardsToAdd.length}, pending ${this.#pendingNextCards.length}`);

        if (cardsToAdd.length > 0) {
          cardsBuffer.push(...cardsToAdd);
          totalNewCardsAdded += cardsToAdd.length;
        }
      }
    } catch (err) {
      console.warn('PaginatedList: error rendering next page:', err);
    } finally {
      this.#removeLoadingImage();
    }

    // Append all collected cards at once to avoid partial-batch grid misalignment
    if (cardsBuffer.length > 0) {
      grid.append(...cardsBuffer);
      this.updatedCallback();
    }

    if (totalNewCardsAdded === 0 && this.#pendingNextCards.length === 0) {
      this.#nextFailures++;
      const lastPage = Number(grid.dataset.lastPage) || 0;
      if ((frontierPage !== null && /** @type {number} */ (frontierPage) >= lastPage) || this.#nextFailures >= 3) {
        this.#hasMoreNext = false;
      }
    } else {
      this.#nextFailures = 0;
      // If frontier has reached or passed the last page AND no pending cards, no more to load
      const lastPage = Number(grid.dataset.lastPage) || 0;
      if (frontierPage !== null && /** @type {number} */ (frontierPage) >= lastPage && this.#pendingNextCards.length === 0) {
        this.#hasMoreNext = false;
      }
    }

    console.log('[Pagination] renderNext END', {
      added: totalNewCardsAdded,
      frontier: frontierPage,
      gridChildren: grid.children.length,
      hasMore: this.#hasMoreNext,
    });

    this.#nextFrontierPage = frontierPage;

    this.#aspectRatioHelper?.processNewElements();

    if (this.#hasMoreNext) {
      requestIdleCallback(() => {
        if (frontierPage !== null) {
          this.#fetchSpecificPage(frontierPage + 1);
        } else {
          this.#prefetchPage('next');
        }
      });
    }
  }

  async #renderPreviousPage() {
    const { grid } = this.refs;
    if (!grid) return;

    const TARGET_CARDS = 12;
    let totalNewCardsAdded = 0;
    let frontierPage = this.#previousFrontierPage;
    const seen = this.#getSeenItems(grid);

    const initialScrollTop = window.scrollY;
    const firstElement = grid.firstElementChild;
    const initialTopOffset = firstElement ? firstElement.getBoundingClientRect().top + window.scrollY : 0;
    const cardsBuffer = [];

    this.#addLoadingImageAfterGrid();

    try {
      while (totalNewCardsAdded < TARGET_CARDS) {
        // First drain any leftover cards from a previous partial page
        if (this.#pendingPreviousCards.length > 0) {
          const remaining = TARGET_CARDS - totalNewCardsAdded;
          const batch = this.#pendingPreviousCards.splice(0, remaining);
          if (batch.length > 0) {
            for (const el of batch) {
              const h = /** @type {HTMLElement} */ (el);
              const name = h.dataset?.collectionName;
              if (name) seen.seenCollections.add(name);
              const pid = h.dataset?.productId;
              if (pid) seen.seenProductIds.add(pid);
            }
            cardsBuffer.push(...batch);
            totalNewCardsAdded += batch.length;
          }
          continue;
        }

        const previousPage = this.#getNextPageInfo('previous', frontierPage);
        if (!previousPage || !this.#shouldUsePage(previousPage)) break;

        let previousPageItemElements = this.#getGridForPage(previousPage.page);

        if (!previousPageItemElements) {
          await this.#fetchSpecificPage(previousPage.page, previousPage.url);
          previousPageItemElements = this.#getGridForPage(previousPage.page);
          if (!previousPageItemElements) break;
        }

        frontierPage = previousPage.page;

        const newCards = this.#filterDuplicateCards(previousPageItemElements, seen);
        const remaining = TARGET_CARDS - totalNewCardsAdded;
        const cardsToAdd = newCards.slice(0, remaining);
        if (newCards.length > remaining) {
          this.#pendingPreviousCards = newCards.slice(remaining);
        }

        if (cardsToAdd.length > 0) {
          cardsBuffer.push(...cardsToAdd);
          totalNewCardsAdded += cardsToAdd.length;
        }
      }
    } catch (err) {
      console.warn('PaginatedList: error rendering previous page:', err);
    } finally {
      this.#removeLoadingImage();
    }

    // Append all collected cards at once to avoid partial-batch grid misalignment
    if (cardsBuffer.length > 0) {
      const heroCard = grid.querySelector('[data-hero-card]');
      if (heroCard) {
        heroCard.after(...cardsBuffer);
      } else {
        grid.prepend(...cardsBuffer);
      }
      this.updatedCallback();
    }

    if (totalNewCardsAdded === 0 && this.#pendingPreviousCards.length === 0) {
      this.#previousFailures++;
      if ((frontierPage !== null && /** @type {number} */ (frontierPage) <= 1) || this.#previousFailures >= 3) {
        this.#hasMorePrevious = false;
      }
    } else {
      this.#previousFailures = 0;
      if (frontierPage !== null && /** @type {number} */ (frontierPage) <= 1 && this.#pendingPreviousCards.length === 0) {
        this.#hasMorePrevious = false;
      }
    }

    this.#previousFrontierPage = frontierPage;

    this.#aspectRatioHelper?.processNewElements();

    if (firstElement) {
      const newTopOffset = firstElement.getBoundingClientRect().top + window.scrollY;
      const heightDiff = newTopOffset - initialTopOffset;
      window.scrollTo({
        top: initialScrollTop + heightDiff,
        behavior: 'instant',
      });
    }

    if (this.#hasMorePrevious) {
      requestIdleCallback(() => {
        if (frontierPage !== null) {
          this.#fetchSpecificPage(frontierPage - 1);
        } else {
          this.#prefetchPage('previous');
        }
      });
    }
  }

  // ---------------------------------------------------------------------------
  // Helpers
  // ---------------------------------------------------------------------------

  /**
   * @param {number} page
   * @returns {Element[] | undefined}
   */
  #getGridForPage(page) {
    const pageHTML = this.pages.get(page);
    if (!pageHTML) return;

    const parsedPage = new DOMParser().parseFromString(pageHTML, 'text/html');
    const gridElement = parsedPage.querySelector('[ref="grid"]');
    if (!gridElement) {
      console.log(`[Pagination] grid(${page}) — no [ref="grid"] in HTML, snippet: ${pageHTML.substring(0, 200)}`);
      return;
    }

    const items = gridElement.querySelectorAll(':scope > [ref="cards[]"]');
    const filtered = Array.from(items).filter(el => !el.hasAttribute('data-hero-card'));
    console.log(`[Pagination] grid(${page}) — ${items.length} items, ${filtered.length} after hero filter`);
    return filtered;
  }

  get sectionId() {
    const id = this.getAttribute('section-id');
    if (!id) throw new Error('The section-id attribute is required');
    return id;
  }

  #handleFilterUpdate = () => {
    this.pages.clear();
    this.#failedPages.clear();
    this.#pageFailCounts.clear();
    this.#inFlightPages.clear();
    this.#pendingNextCards = [];
    this.#pendingPreviousCards = [];
    this.#isRendering = false;
    this.#nextFrontierPage = null;
    this.#previousFrontierPage = null;
    this.#hasMoreNext = true;
    this.#hasMorePrevious = true;
    this.#nextFailures = 0;
    this.#previousFailures = 0;

    if (this.infinityScrollObserver) {
      this.infinityScrollObserver.disconnect();
      this.infinityScrollObserver = undefined;
    }

    const { grid } = this.refs;
    if (!grid) return;

    const reinit = () => {
      if (!this.isConnected) return;
      this.#observeViewMore();
      this.#prefetchPage('next');
    };

    const observer = new MutationObserver(() => {
      observer.disconnect();
      setTimeout(reinit, 500);
    });

    observer.observe(grid, {
      attributes: true,
      childList: true,
      subtree: true,
    });

    setTimeout(() => {
      observer.disconnect();
      reinit();
    }, 5000);
  };
}
